import { useState } from 'react'
import { ChevronDown, ChevronRight, Bug, CheckCircle, XCircle, AlertTriangle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useDebug } from '@/contexts/DebugContext'
import { JsonText } from '@/components/common/JsonText'

interface ValidationError {
  path: string[]
  message: string
}

interface DebugPanelProps {
  /** Raw form data to display */
  data: unknown
  /** Panel title */
  title?: string
  /** Zod validation errors (if any) */
  validationErrors?: ValidationError[]
  /** Transformed data that would be sent to backend */
  transformedData?: unknown
  /** Allow showing in production (only for admin routes) */
  showInProd?: boolean
  /** Additional CSS classes */
  className?: string
  /** Start expanded */
  defaultOpen?: boolean
  /** Type of debug panel (controls visibility via context) */
  type?: 'form' | 'reservation' | 'api'
}

/**
 * DebugPanel - Development-only component for debugging form data
 * 
 * Usage:
 * - Public forms: Only visible in development (import.meta.env.DEV)
 * - Admin forms: Can be visible in production with showInProd={true}
 * 
 * @example
 * // Public form (auto-hidden in production)
 * <DebugPanel data={watch()} validationErrors={errors} />
 * 
 * // Admin form (visible in production for admins)
 * <DebugPanel data={watch()} showInProd={isAdmin} />
 */
export function DebugPanel({
  data,
  title = 'Debug: Raw Data',
  validationErrors = [],
  transformedData,
  showInProd = false,
  className,
  defaultOpen = false,
  type
}: DebugPanelProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen)
  const [activeTab, setActiveTab] = useState<'raw' | 'errors' | 'transformed'>('raw')
  const { flags } = useDebug()

  // Check visibility based on type and debug flags
  const shouldShow = () => {
    // Always show in dev mode
    if (import.meta.env.DEV) return true
    
    // Check debug flags for specific types
    if (type === 'form' && flags.showFormData) return true
    if (type === 'reservation' && flags.showReservationData) return true
    if (type === 'api' && flags.showApiCalls) return true
    
    // Fallback to showInProd prop
    return showInProd
  }

  if (!shouldShow()) {
    return null
  }

  const hasErrors = validationErrors.length > 0
  const hasTransformed = transformedData !== undefined

  const tabs = [
    { id: 'raw' as const, label: 'Raw Data', icon: Bug },
    { id: 'errors' as const, label: `Errors (${validationErrors.length})`, icon: hasErrors ? XCircle : CheckCircle },
    ...(hasTransformed ? [{ id: 'transformed' as const, label: 'Transformed', icon: AlertTriangle }] : [])
  ]

  return (
    <div className={cn(
      'mt-4 border rounded-lg overflow-hidden',
      'bg-slate-900 text-slate-100',
      'font-mono text-xs',
      className
    )}>
      {/* Header */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'w-full flex items-center gap-2 px-4 py-2',
          'bg-slate-800 hover:bg-slate-700 transition-colors',
          'text-left'
        )}
      >
        {isOpen ? (
          <ChevronDown className="h-4 w-4 text-slate-400" />
        ) : (
          <ChevronRight className="h-4 w-4 text-slate-400" />
        )}
        <Bug className="h-4 w-4 text-amber-500" />
        <span className="text-slate-300">{title}</span>
        {hasErrors && (
          <span className="ml-auto px-2 py-0.5 bg-red-500/20 text-red-400 rounded text-xs">
            {validationErrors.length} errors
          </span>
        )}
        {!hasErrors && (
          <span className="ml-auto px-2 py-0.5 bg-green-500/20 text-green-400 rounded text-xs">
            Valid
          </span>
        )}
      </button>

      {/* Content */}
      {isOpen && (
        <div className="border-t border-slate-700">
          {/* Tabs */}
          <div className="flex border-b border-slate-700">
            {tabs.map((tab) => {
              const Icon = tab.icon
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={cn(
                    'flex items-center gap-1.5 px-4 py-2 text-xs transition-colors',
                    activeTab === tab.id
                      ? 'bg-slate-700 text-white border-b-2 border-amber-500'
                      : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800'
                  )}
                >
                  <Icon className={cn(
                    'h-3.5 w-3.5',
                    tab.id === 'errors' && hasErrors && 'text-red-400',
                    tab.id === 'errors' && !hasErrors && 'text-green-400'
                  )} />
                  {tab.label}
                </button>
              )
            })}
          </div>

          {/* Tab Content */}
          <div className="p-4 max-h-80 overflow-auto">
            {activeTab === 'raw' && (
              <JsonText data={data} />
            )}

            {activeTab === 'errors' && (
              <ErrorList errors={validationErrors} />
            )}

            {activeTab === 'transformed' && hasTransformed && (
              <JsonText data={transformedData} />
            )}
          </div>
        </div>
      )}
    </div>
  )
}

/** Error List Component */
function ErrorList({ errors }: { errors: ValidationError[] }) {
  if (errors.length === 0) {
    return (
      <div className="flex items-center gap-2 text-green-400">
        <CheckCircle className="h-4 w-4" />
        <span>No validation errors</span>
      </div>
    )
  }

  return (
    <ul className="space-y-2">
      {errors.map((error, index) => (
        <li key={index} className="flex items-start gap-2 text-red-400">
          <XCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <div>
            <span className="text-slate-300">{error.path.join('.')}</span>
            <span className="text-slate-500 mx-1">→</span>
            <span>{error.message}</span>
          </div>
        </li>
      ))}
    </ul>
  )
}

export default DebugPanel
